import { PageSeo } from "@components/seo/PageSeo";
import type {
    IModuleHeadline,
    IModuleHeadlineFields,
    IPageCampaigns,
    ISiteSettings,
} from "@contentful-api/types/contentful";
import type { NextPageWithLayout } from "@interfaces/PageInterfaces";
import { campaignsPage } from "@lib/constants/pagePaths";
import { ModulesRenderer } from "@lib/contentful/modules-renderer/ModulesRenderer";
import { getDefaultLayout } from "@lib/utils/layoutUtility";
import { deserialize } from "@ungap/structured-clone";
import type { GetStaticPaths, GetStaticPropsContext, PreviewData } from "next";
import type { ParsedUrlQuery } from "querystring";
import type { EditorialPageProps } from "templates/EditorialPage";
import { generateGetStaticPropsForEditorialPage } from "templates/EditorialPage";
import dynamic from "next/dynamic";

const Headline = dynamic(() =>
    import("@components/cms/headline/Headline").then((mod) => mod.Headline)
);

const CampaignsPage: NextPageWithLayout<EditorialPageProps> = ({
    serializedSiteSettings,
    serializedPage,
    pathName,
}) => {
    const page: IPageCampaigns = deserialize(serializedPage);
    const siteSettings: ISiteSettings = deserialize(serializedSiteSettings);

    const {
        fields: {
            modules,
            seoMetadata,
            title,
            titleSize = "Extra Large",
            desc,
            descriptionSize = "Normal",
            callToActions,
        } = {},
    } = page;
    const { fields: { authorizationInvitation } = {} } = siteSettings;

    return (
        <>
            {seoMetadata && <PageSeo seoModule={seoMetadata} pathName={pathName} />}
            <div hidden aria-hidden="true" data-testid="campaignsSlug" />
            {(title || desc) && (
                <Headline
                    module={
                        {
                            fields: {
                                title,
                                desc,
                                callToActions,
                                // We need to cast because the types are not exactly the same
                                titleSize: titleSize as IModuleHeadlineFields["titleSize"],
                                descriptionSize,
                            },
                        } as IModuleHeadline
                    }
                    isModule={false}
                />
            )}
            <ModulesRenderer modules={modules} authorizationInvitation={authorizationInvitation} />
        </>
    );
};

export default CampaignsPage;

CampaignsPage.getLayout = getDefaultLayout;

export const getStaticProps = async (
    context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>
) => {
    return generateGetStaticPropsForEditorialPage(context, campaignsPage);
};

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: "blocking",
    };
};
